import { useCallback, useEffect, useState } from "react";
import Cookies from "js-cookie";

import { API_PROJECT_WORKSTREAM_TRACKING } from "../../../config/Endpoints.js";
import { deleteApi, getApi, postApi, putApi } from "../../utils/api.ts";
import type {
  ProjectMember,
  ProjectCategory,
  WorkstreamConfiguration,
  WorkstreamInput,
  ConfiguredWorkstream,
  ProjectPhase,
} from "./types.ts";

function unwrap<T>(raw: unknown): T {
  if (raw && typeof raw === "object" && "data" in raw) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

export function useWorkstreamConfiguration(projectId: string) {
  const [configuration, setConfiguration] = useState<WorkstreamConfiguration | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [categories, setCategories] = useState<ProjectCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = Cookies.get("accessToken");
  const projectBase = `${API_PROJECT_WORKSTREAM_TRACKING}/projects/${projectId}`;

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!projectId) {
      setConfiguration(null);
      setMembers([]);
      setCategories([]);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [configResponse, membersResponse, categoriesResponse] = await Promise.all([
        getApi<unknown>(`${projectBase}/configuration`, token, "json", signal),
        getApi<unknown>(`${projectBase}/members?includeInactive=true`, token, "json", signal),
        getApi<unknown>(`${projectBase}/categories?includeInactive=true`, token, "json", signal),
      ]);
      if (!signal?.aborted) {
        setConfiguration(unwrap<WorkstreamConfiguration>(configResponse.data));
        setMembers(unwrap<ProjectMember[]>(membersResponse.data) ?? []);
        setCategories(unwrap<ProjectCategory[]>(categoriesResponse.data) ?? []);
      }
    } catch (e) {
      if (e instanceof Error && e.name === "AbortError") return;
      if (!signal?.aborted) setError(e instanceof Error ? e.message : "Unable to load configuration");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [projectBase, projectId, token]);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const saveTracking = async (input: {
    sowRate: number;
    salesCommissionPercent: number | null;
    totalHours: number;
    discoveryHours: number | null;
    solutionArchitectureHours: number | null;
    developmentHours: number | null;
    uatHours: number | null;
    discoveryWeeks: number | null;
    solutionArchitectureWeeks: number | null;
    developmentWeeks: number | null;
    uatWeeks: number | null;
    trackable: boolean;
    currency: string;
    phases: ProjectPhase[];
  }) => {
    await putApi(`${projectBase}/tracking`, token, input);
    await load();
  };

  const saveDesignation = async (input: { designation: string; globalDesignationId?: number; commercialRate: number | null }, id?: number) => {
    const url = id ? `${projectBase}/designations/${id}` : `${projectBase}/designations`;
    if (id) await putApi(url, token, input);
    else await postApi(url, token, input);
    await load();
  };

  const saveCategory = async (name: string, id?: number) => {
    const url = id ? `${projectBase}/categories/${id}` : `${projectBase}/categories`;
    if (id) await putApi(url, token, { name });
    else await postApi(url, token, { name });
    await load();
  };

  const updateCategoryStatus = async (categoryId: number, status: number) => {
    await putApi(`${projectBase}/categories/${categoryId}/status`, token, { status });
    await load();
  };

  const removeCategory = async (categoryId: number) => {
    await deleteApi(`${projectBase}/categories/${categoryId}`, token);
    await load();
  };

  const saveMemberRate = async (memberRateId: number, commercialRate: number | null) => {
    await putApi(`${projectBase}/member-rates/${memberRateId}`, token, { commercialRate });
    await load();
  };

  const saveWorkstream = async (input: WorkstreamInput, id?: number) => {
    const url = id ? `${projectBase}/workstreams/${id}` : `${projectBase}/workstreams`;
    if (id) await putApi(url, token, input);
    else await postApi(url, token, input);
    await load();
  };

  const getWorkstream = async (workstreamId: number) => {
    const response = await getApi<unknown>(`${projectBase}/workstreams/${workstreamId}`, token);
    return unwrap<ConfiguredWorkstream>(response.data);
  };

  const saveWorkstreamMember = async (
    workstreamId: number,
    projectMemberId: number,
    allocatedHours: number,
    workstreamMemberId?: number,
  ) => {
    const body = { projectMemberId, allocatedHours };
    const url = `${projectBase}/workstreams/${workstreamId}/members${workstreamMemberId ? `/${workstreamMemberId}` : ""}`;
    if (workstreamMemberId) await putApi(url, token, body);
    else await postApi(url, token, body);
    await load();
    return getWorkstream(workstreamId);
  };

  const removeWorkstreamMember = async (workstreamId: number, workstreamMemberId: number) => {
    await deleteApi(`${projectBase}/workstreams/${workstreamId}/members/${workstreamMemberId}`, token);
    await load();
    return getWorkstream(workstreamId);
  };

  return { configuration, members, categories, loading, error, reload: load, saveTracking, saveDesignation, saveCategory, updateCategoryStatus, removeCategory, saveMemberRate, saveWorkstream, getWorkstream, saveWorkstreamMember, removeWorkstreamMember };
}
